/* eslint-disable simple-import-sort/imports */
import React from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { isDefined } from '../../../../../../utils/variables';
import { Icon } from '../../../../icon/icon';
import { AssetTagChip } from '../../components/asset-tag-chip';
import { useAssetFilterStore } from '../../../state/asset-filter-store';
import { Stack } from '../../../../stack/stack';
import { ProjectGeodataManager } from '../../../../../../viewer/project-geodata-manager';
// Dropdown moved to dedicated dialog; no direct dropdown here

type Props = {
  onEditCreatedAt: () => void;
  onEditCreatedBy: () => void;
  onEditImageCapturedAt: () => void;
  onEditTags: () => void;
  onEditType: () => void;
  onEditTimeline: () => void;
};

const formatDate = (iso?: string) => (iso ? new Date(iso).toLocaleDateString() : '');

function renderClearButton(onClear: () => void) {
  return (
    <ActionIconButton
      aria-label={undefined}
      title={undefined}
      onClick={(e) => {
        e.stopPropagation();
        onClear();
      }}
    >
      <Icon icon={['fal', 'times']} />
    </ActionIconButton>
  );
}

export const FilterTable: React.FC<Props> = (props) => {
  const { t } = useTranslation();
  const {
    onEditCreatedAt,
    onEditCreatedBy,
    onEditImageCapturedAt,
    onEditTags,
    onEditType,
    onEditTimeline,
  } = props;
  const {
    createdAt,
    imageCapturedAt,
    createdBy,
    assetCategoryType,
    tagGroups,
    timelineContainerId,
    applyCreatedAtFilter,
    applyImageCapturedAtFilter,
    applyCreatedByFilter,
    applyAssetTypeFilter,
    applyTagGroupsFilter,
    applyTimelineFilter,
  } = useAssetFilterStore();

  const timelineFilterOption = React.useMemo(() => {
    if (timelineContainerId === 'unset') {
      return { id: 'unset', name: t('timeline.dropDown.unset', { ns: 'skyviewAssetMenu' }) };
    }
    const timeline = ProjectGeodataManager.instance()
      .getDetailedTimelineItems()
      .find((x) => x.id === timelineContainerId);
    return timeline && { id: timeline.id, name: timeline.dateFormattedWithNumber };
  }, [timelineContainerId, t]);

  const assetCategoryTypeLabel = React.useMemo(() => {
    switch (assetCategoryType) {
      case 'Design':
        return t('design.categoryName', { ns: 'skyviewAssetMenu' });
      case 'Area':
        return t('area.categoryName', { ns: 'skyviewAssetMenu' });
      case 'Line':
        return t('line.categoryName', { ns: 'skyviewAssetMenu' });
      case 'Poi':
        return t('poi.categoryName', { ns: 'skyviewAssetMenu' });
      case 'CAD':
        return t('cad.categoryName', { ns: 'skyviewAssetMenu' });
      case 'Markmodeller':
        return t('terrainModel.categoryName', { ns: 'skyviewAssetMenu' });
      default:
        return assetCategoryType ?? '';
    }
  }, [assetCategoryType, t]);

  return (
    <TableGrid>
      {createdAt && (createdAt.createdAtFrom || createdAt.createdAtTo) && (
        <CreatedAtRow role="button" onClick={onEditCreatedAt}>
          <IconCell>
            <Icon icon={['fal', 'calendar']} />
          </IconCell>
          <ContentCell>
            {`${formatDate(createdAt.createdAtFrom)} – ${formatDate(createdAt.createdAtTo)}`}
          </ContentCell>
          <ActionsCell>
            {renderClearButton(() => void Promise.resolve(applyCreatedAtFilter(undefined)))}
          </ActionsCell>
        </CreatedAtRow>
      )}

      {imageCapturedAt &&
        (imageCapturedAt.imageCapturedAtFrom || imageCapturedAt.imageCapturedAtTo) && (
          <ImageCapturedAtRow role="button" onClick={onEditImageCapturedAt}>
            <IconCell>
              <Icon icon={['fal', 'camera']} />
            </IconCell>
            <ContentCell>
              {`${formatDate(imageCapturedAt.imageCapturedAtFrom)} – ${formatDate(imageCapturedAt.imageCapturedAtTo)}`}
            </ContentCell>
            <ActionsCell>
              {renderClearButton(() => void Promise.resolve(applyImageCapturedAtFilter(undefined)))}
            </ActionsCell>
          </ImageCapturedAtRow>
        )}

      {isDefined(createdBy) && (
        <CreatedByRow role="button" onClick={onEditCreatedBy}>
          <IconCell>
            <Icon icon={['fal', 'user']} />
          </IconCell>
          <ContentCell>{createdBy.title ?? createdBy.id}</ContentCell>
          <ActionsCell>
            {renderClearButton(() => void Promise.resolve(applyCreatedByFilter(undefined)))}
          </ActionsCell>
        </CreatedByRow>
      )}

      {assetCategoryType && (
        <AssetCategoryRow role="button" onClick={onEditType}>
          <IconCell>
            <Icon icon={['fal', 'layer-group']} />
          </IconCell>
          <ContentCell>{assetCategoryTypeLabel}</ContentCell>
          <ActionsCell>
            {renderClearButton(() => void Promise.resolve(applyAssetTypeFilter(undefined)))}
          </ActionsCell>
        </AssetCategoryRow>
      )}

      {isDefined(timelineContainerId) && (
        <TimelineRow role="button" onClick={onEditTimeline}>
          <IconCell>
            <Icon icon={['fal', 'clock']} />
          </IconCell>
          <ContentCell>{timelineFilterOption?.name}</ContentCell>
          <ActionsCell>
            {renderClearButton(() => void Promise.resolve(applyTimelineFilter(undefined)))}
          </ActionsCell>
        </TimelineRow>
      )}

      {isDefined(tagGroups) && tagGroups.length > 0 && (
        <TagsRow role="button" onClick={onEditTags}>
          <IconCell>
            <Icon icon={['fal', 'tags']} />
          </IconCell>
          <ContentCell>
            <TagsChips alignItems="center" direction="row" flexWrap="nowrap" spacing={0.25}>
              {tagGroups.map((group, groupIndex) => (
                <React.Fragment key={groupIndex}>
                  {groupIndex > 0 && <Icon icon={['fal', 'slash-forward']} />}
                  {group.map((tag) => (
                    <AssetTagChip key={tag} tag={{ id: tag }} />
                  ))}
                </React.Fragment>
              ))}
            </TagsChips>
          </ContentCell>
          <ActionsCell>
            {renderClearButton(() => void Promise.resolve(applyTagGroupsFilter(undefined)))}
          </ActionsCell>
        </TagsRow>
      )}
    </TableGrid>
  );
};

const TableGrid = styled.div`
  display: grid;
  grid-template-columns: auto 1fr auto;
  align-items: center;
  row-gap: 0.4em;
`;

const RowContent = styled.div`
  display: contents;
`;

const CreatedAtRow = styled(RowContent)``;
const ImageCapturedAtRow = styled(RowContent)``;
const CreatedByRow = styled(RowContent)``;
const AssetCategoryRow = styled(RowContent)``;
const TimelineRow = styled(RowContent)``;
const TagsRow = styled(RowContent)``;

const IconCell = styled.div`
  width: 1.6em;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #555;
`;

const ContentCell = styled.div`
  font-size: 12px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
`;

const ActionsCell = styled.div`
  display: inline-flex;
  gap: 0.4em;
`;

const ActionIconButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  padding: 0.2em;
  cursor: pointer;
`;

const TagsChips = styled(Stack)`
  overflow: hidden;
`;
